import React, { useMemo } from "react";

import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import { HelpBox } from "@/components/atoms";
import { useInsightFilters } from "@/context";
import { InsightResult } from "@/types";
import { tagTypeColorMap, toIndex, toPercentage } from "@/utils";

interface ScoreTabProps {
  data: InsightResult[];
}

const MAX_SCORES = 100;

const TopScoreView: React.FC<ScoreTabProps> = ({ data }) => {
  const theme = useTheme();
  const {
    filters: { sortType, scoreType },
  } = useInsightFilters();

  const scores = useMemo(() => {
    const filtered = data.filter(
      (x) => x[scoreType.value as keyof InsightResult],
    );
    const sorted = [...filtered].sort((a, b) => {
      const aValue = a[scoreType.value as keyof InsightResult] as number;
      const bValue = b[scoreType.value as keyof InsightResult] as number;
      return sortType === "asc" ? aValue - bValue : bValue - aValue;
    });
    return sorted.slice(0, MAX_SCORES);
  }, [data, scoreType.value, sortType]);

  const renderScoreValue = (variable: InsightResult) => {
    switch (scoreType.value) {
      case "score_rgn":
        return toIndex(variable.score_rgn);
      case "score_wt":
        return toIndex(variable.score_wt ?? 0);
      case "rate":
        return toPercentage(variable.rate, 1);
      default:
        return "";
    }
  };

  const toCategoryLabel = (variable: InsightResult) => {
    if (variable.tag_type === variable.tag_group) {
      return `${variable.tag_type} > ${variable.tag_category}`;
    } else {
      return `${variable.tag_type} > ${variable.tag_group} > ${variable.tag_category}`;
    }
  };

  const getTagTypeColor = (tagType: string) =>
    tagTypeColorMap[tagType as keyof typeof tagTypeColorMap] ??
    theme.palette.primary.main;

  return (
    <Grid display="flex" container spacing={2}>
      {scores.map((variable, idx) => (
        <Grid key={`${variable.tag_name}-${idx}`}>
          <Card
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              border: 1,
              borderRadius: 1,
              borderColor: theme.palette.divider,
              width: 256,
            }}
          >
            <CardHeader
              disableTypography
              title={
                <HelpBox title={toCategoryLabel(variable)}>
                  <Chip
                    label={variable.tag_category}
                    size="small"
                    sx={{
                      alignSelf: "flex-start",
                      backgroundColor: getTagTypeColor(variable.tag_type),
                      color: theme.palette.getContrastText(
                        getTagTypeColor(variable.tag_type),
                      ),
                    }}
                  />
                </HelpBox>
              }
              sx={{ pb: 0 }}
            />
            <CardContent
              sx={{
                pb: 2,
                "&:last-child": { pb: 2 },
              }}
            >
              <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
              >
                <HelpBox title={variable.tag_name}>
                  <Typography variant="body1" noWrap sx={{ maxWidth: 150 }}>
                    {variable.tag_name}
                  </Typography>
                </HelpBox>
                <Typography variant="body1">
                  {renderScoreValue(variable)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default TopScoreView;
